import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Tooltip, Typography } from "@mui/material";
import { useState, useRef } from "react";
import useLanguage from "../../hooks/useLanguage";
import {
  Chunk,
  FileCard,
  FileInfoChunk,
  FileUploadArea,
  FileVerticalChunk,
  LoadingBar,
  OverflowTypography,
  RelativeRow,
  RemoveIconButton,
} from "./styles";

function SingleFileCard({ file, onRemove, loading }) {
  return (
    <FileCard variant="outline">
      <RelativeRow>
        <FileVerticalChunk>
          <InsertDriveFileRoundedIcon fontSize="large" />
          <FileInfoChunk>
            <Tooltip title={file.name}>
              <OverflowTypography variant="body1">{file.name}</OverflowTypography>
            </Tooltip>
            <OverflowTypography variant="caption" color="text.secondary">
              {file.size} bytes
            </OverflowTypography>
          </FileInfoChunk>
        </FileVerticalChunk>
        <RemoveIconButton>
          <CloseRoundedIcon fontSize="small" onClick={onRemove} />
        </RemoveIconButton>
      </RelativeRow>
      {loading && <LoadingBar />}
    </FileCard>
  );
}

export default function UploadArea({ file, setFile, loading, multiple, files, onFilesChange, accept }) {
  const [
    {
      components: { uploadArea: t },
    },
  ] = useLanguage();

  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length === 0) return;
    if (multiple) {
      onFilesChange?.([...(files || []), ...dropped]);
    } else {
      setFile?.(dropped[0]);
    }
  };

  const handleClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length === 0) return;
    if (multiple) {
      onFilesChange?.([...(files || []), ...selected]);
    } else {
      setFile?.(selected[0]);
    }
    e.target.value = "";
  };

  const handleRemoveAt = (index) =>
    onFilesChange?.((files || []).filter((_, i) => i !== index));

  const dropZone = (
    <FileUploadArea
      isDragOver={isDragOver}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <Chunk>
        <CloudUploadRoundedIcon fontSize="large" />
        <Typography variant="subtitle1" color="text.secondary" fontSize={10}>
          {t.subtitle}
        </Typography>
      </Chunk>
    </FileUploadArea>
  );

  if (multiple) {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
          accept={accept || "*/*"}
        />
        {(files || []).map((f, i) => (
          <SingleFileCard
            key={i}
            file={f}
            onRemove={() => handleRemoveAt(i)}
            loading={loading}
          />
        ))}
        {dropZone}
      </>
    );
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept={accept || "*/*"}
      />
      {file ? (
        <SingleFileCard file={file} onRemove={() => setFile?.(null)} loading={loading} />
      ) : (
        dropZone
      )}
    </>
  );
}
